import type { ReactElement, RefObject } from "react";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import NextLink from "next/link";
import { useRouter } from "next/router";
import type { ChakraProps, MenuButtonProps } from "@chakra-ui/react";
import {
  Avatar,
  Button,
  Center,
  HStack,
  Hide,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Show,
  Skeleton,
  SkeletonCircle,
  Spacer,
  Tooltip,
  chakra,
} from "@chakra-ui/react";
import { RiArrowDownSLine, RiMenuLine, RiQuestionLine } from "react-icons/ri";
import { useAppDrawer } from "stores/use-app-drawer";
import useSessionObjective from "stores/use-session-objective";
import usePermission from "hooks/use-permission";
import useUser from "hooks/use-user";
import ActiveLink from "../app-nav/active-link";
import AppHeaderLogo from "./app-header-logo";
import ListDropdown from "./list-dropdown";
import SearchDropdown from "./search-dropdown";

const AppHeaderSearch = dynamic(() => import("./app-header-search"), {
  suspense: true,
});

const SiteOptionsMenu = ({
  disableMenuItems = false,
  ...props
}: MenuButtonProps & { disableMenuItems?: boolean }): ReactElement => {
  const user = useUser();
  const directAccess = user?.details?.directAccess || false;
  const manageAccountSettings = usePermission("manageAccountSettings");

  const { onSessionObjectiveOpen } = useSessionObjective((store) => ({
    onSessionObjectiveOpen: store.onOpen,
  }));

  return (
    <Menu>
      <MenuButton
        aria-label="Site Options Menu"
        as={Button}
        rightIcon={<Icon as={RiArrowDownSLine} color="white" />}
        bg="transparent"
        fontWeight="500"
        _hover={{
          bg: "blackAlpha.300",
        }}
        _focus={{
          boxShadow: "outline-light",
        }}
        _active={{
          bg: "blackAlpha.400",
        }}
        _expanded={{
          bg: "blackAlpha.400",
        }}
        px={2}
        display="flex"
        data-cy="site-options-menu-button"
        {...props}
      >
        <Avatar
          name={user?.details?.username}
          size="sm"
          bg="white"
          color="blue.600"
        />
      </MenuButton>
      <Portal>
        <MenuList zIndex="dropdown">
          <MenuGroup title={user?.details?.username || "Profile"}>
            {!disableMenuItems && manageAccountSettings && (
              <MenuItem as={NextLink} href="/account">
                Account
              </MenuItem>
            )}
            {disableMenuItems && (
              <MenuItem as={NextLink} href="/upgrade">
                Upgrade
              </MenuItem>
            )}
            {!disableMenuItems && !manageAccountSettings && !directAccess && (
              <MenuItem as={NextLink} href="/account/change-password">
                Change Password
              </MenuItem>
            )}
            {!disableMenuItems && user?.access?.sessionObjective && (
              <MenuItem onClick={onSessionObjectiveOpen}>
                Session Objective
              </MenuItem>
            )}
            <MenuItem onClick={() => user.signOut()}>Sign Out</MenuItem>
          </MenuGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
};

interface AppDrawerButtonProps extends ChakraProps {
  drawerButtonRef: RefObject<HTMLButtonElement>;
}

const AppDrawerButton = ({
  drawerButtonRef,
  ...props
}: AppDrawerButtonProps): ReactElement => {
  const { onAppDrawerOpen } = useAppDrawer((store) => ({
    onAppDrawerOpen: store.onOpen,
  }));

  return (
    <chakra.button
      as="button"
      role="button"
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onAppDrawerOpen}
      ref={drawerButtonRef}
      aria-label="Open Menu"
      h="40px"
      w="40px"
      rounded="md"
      ml={2}
      flex="0 0 auto"
      outline="none"
      _focus={{ boxShadow: "outline-light", bg: "blue.600" }}
      {...props}
    >
      <Icon as={RiMenuLine} color="white" h="32px" w="32px" />
    </chakra.button>
  );
};

interface AppHeaderProps {
  drawerButtonRef: RefObject<HTMLButtonElement>;
}

const AppHeader = ({ drawerButtonRef }: AppHeaderProps): ReactElement => {
  const user = useUser();
  const isFree = user?.access?.group === "free";
  const { asPath } = useRouter();
  return (
    <HStack
      as="header"
      bg="blue.500"
      boxShadow="sm"
      zIndex={5}
      px={[2, 2, 4]}
      data-cy="app-header"
      spacing={[2, 3]}
    >
      <AppHeaderLogo />

      {user.isLoggedIn && !isFree && (
        <Suspense
          fallback={
            <Skeleton
              flex="0 1 500px"
              opacity={0.3}
              borderRadius="md"
              h="40px"
            />
          }
        >
          <AppHeaderSearch />
        </Suspense>
      )}

      <Spacer sx={{ margin: "0 !important" }} />

      <Show above="lg">
        {user.isLoggedIn && !isFree && (
          <>
            <ActiveLink href="/">Dashboard</ActiveLink>
            <SearchDropdown />
            <ListDropdown />
          </>
        )}

        {user.isLoggedIn && isFree && (
          <Button
            as={NextLink}
            href="/upgrade"
            colorScheme="amber"
            rounded="lg"
            size="md"
            _focus={{
              boxShadow: "outline-light",
            }}
            display="flex"
            data-cy="click-upgrade-header-button"
            flexShrink={0}
          >
            Upgrade
          </Button>
        )}

        <Tooltip label="Help Center">
          <IconButton
            aria-label="Help Center"
            as="a"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.privco.com/help-center"
            data-cy="app-header-link-help-center"
            variant="ghost"
            color="white"
            mx={1}
            _hover={{
              bg: "blackAlpha.300",
            }}
            _focus={{
              boxShadow: "outline-light",
            }}
            icon={<Icon as={RiQuestionLine} h={6} w={6} />}
          />
        </Tooltip>

        {!user.isLoggedIn && !user.loading && (
          <Button
            as={NextLink}
            href={`/signin?${new URLSearchParams({ redirect: asPath })}`}
            colorScheme="sky"
            rounded="lg"
            size="md"
            mx={4}
            _focus={{
              boxShadow: "outline-light",
            }}
            display="flex"
            data-cy="click-signin-header-button"
          >
            Sign in
          </Button>
        )}

        {user.isLoggedIn && !user.loading && (
          <SiteOptionsMenu disableMenuItems={isFree} />
        )}

        {user.loading && (
          <Center pl={2} pr={8}>
            <SkeletonCircle size="8" />
          </Center>
        )}
      </Show>

      <Hide above="lg">
        <AppDrawerButton drawerButtonRef={drawerButtonRef} />
      </Hide>
    </HStack>
  );
};

export default AppHeader;
